import { CommonModule } from '@angular/common';
import { Component, Inject, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { AuthorizedApiService } from '@core';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { DateTimePickerFieldComponent } from '@shared/components/date-time-picker-field/date-time-picker-field.component';
import { AttendanceEventCreatePayload, AttendanceEventRecord } from '../attendance-event/attendance-event.interface';
import { AttendanceResultRecalculatePayload, AttendanceResultRecord } from '../attendance-result/attendance-result.interface';

export type MissingPunchSlot = 'entry' | 'meal_start' | 'meal_end' | 'exit';

export interface MissingPunchDialogData {
  result: AttendanceResultRecord;
  employeeName: string;
  employeeNumber: string;
  slot: MissingPunchSlot;
  punches: Partial<Record<MissingPunchSlot, AttendanceEventRecord>>;
}

export interface MissingPunchDialogResult {
  event: AttendanceEventRecord;
  recalculationQueued: boolean;
}

@Component({
  selector: 'app-missing-punch-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, DateTimePickerFieldComponent, MatButtonModule, MatDialogModule, MatFormFieldModule, MatIconModule, MatInputModule, TranslateModule],
  templateUrl: './missing-punch-dialog.component.html',
  styleUrl: './missing-punch-dialog.component.scss',
})
export class MissingPunchDialogComponent {
  eventDate = this.data.result.workDate || '';
  eventTime = '';
  reason = '';
  isSaving = false;

  private readonly api = inject(AuthorizedApiService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);

  constructor(
    private readonly dialogRef: MatDialogRef<MissingPunchDialogComponent, MissingPunchDialogResult | null>,
    @Inject(MAT_DIALOG_DATA) public readonly data: MissingPunchDialogData,
  ) {}

  get slotLabel(): string {
    return this.translate.instant(`missingPunchesCatalog.table.${this.slotTranslationKey}`);
  }

  get formattedWorkDate(): string {
    const [year, month, day] = String(this.data.result.workDate || '').split('-').map(Number);
    if (!year || !month || !day) return this.data.result.workDate || '—';
    return new Intl.DateTimeFormat(this.locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
      .format(new Date(year, month - 1, day));
  }

  get previousPunch(): AttendanceEventRecord | null {
    const index = this.slotOrder.indexOf(this.data.slot);
    for (let cursor = index - 1; cursor >= 0; cursor--) {
      const event = this.data.punches[this.slotOrder[cursor]];
      if (event) return event;
    }
    return null;
  }

  get nextPunch(): AttendanceEventRecord | null {
    const index = this.slotOrder.indexOf(this.data.slot);
    for (let cursor = index + 1; cursor < this.slotOrder.length; cursor++) {
      const event = this.data.punches[this.slotOrder[cursor]];
      if (event) return event;
    }
    return null;
  }

  close(): void {
    if (!this.isSaving) this.dialogRef.close(null);
  }

  async save(): Promise<void> {
    const validation = this.validate();
    if (validation) {
      this.notify('snackbar-warning', validation);
      return;
    }

    this.isSaving = true;
    try {
      const eventAt = this.toUtcIso(this.eventDate, this.eventTime, this.data.result.timezone || 'America/Cancun');
      const payload: AttendanceEventCreatePayload = {
        employeeId: this.data.result.employeeId,
        source: 'manual',
        eventType: this.data.slot,
        eventAt,
        timezone: this.data.result.timezone || 'America/Cancun',
        rawPayload: {
          origin: 'missing_punches',
          attendanceResultId: this.data.result.attendanceResultId,
          workDate: this.data.result.workDate,
          reason: this.reason.trim(),
          manuallyAdded: true,
        },
      };
      const event = await firstValueFrom(this.api.post<AttendanceEventRecord>('attendance/events', payload));
      let recalculationQueued = false;
      try {
        const recalculatePayload: AttendanceResultRecalculatePayload = {
          employeeId: this.data.result.employeeId,
          workDate: this.data.result.workDate,
          reason: 'manual_recalculation',
        };
        await firstValueFrom(this.api.post('attendance-results/recalculate', recalculatePayload));
        recalculationQueued = true;
      } catch (error) {
        console.error('Missing punch created but recalculation could not be queued', error);
      }
      this.dialogRef.close({ event, recalculationQueued });
    } catch (error) {
      this.snackBar.open(
        extractApiErrorMessage(error, this.translate.instant('missingPunchesCatalog.manualPunch.saveError')),
        undefined,
        { duration: 4500, panelClass: ['snackbar-danger'], horizontalPosition: 'center', verticalPosition: 'bottom' },
      );
    } finally {
      this.isSaving = false;
    }
  }

  formatPunch(event: AttendanceEventRecord | null): string {
    if (!event?.eventAt) return '—';
    const date = new Date(event.eventAt);
    if (Number.isNaN(date.getTime())) return '—';
    return new Intl.DateTimeFormat(this.locale, {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
      ...(this.data.result.timezone ? { timeZone: this.data.result.timezone } : {}),
    }).format(date);
  }

  private validate(): string | null {
    if (!this.eventDate) return this.translate.instant('missingPunchesCatalog.manualPunch.validationDate');
    if (!this.eventTime) return this.translate.instant('missingPunchesCatalog.manualPunch.validationTime');
    if (!this.reason.trim()) return this.translate.instant('missingPunchesCatalog.manualPunch.validationReason');
    const eventAt = new Date(this.toUtcIso(this.eventDate, this.eventTime, this.data.result.timezone || 'America/Cancun')).getTime();
    const previous = this.previousPunch?.eventAt ? new Date(this.previousPunch.eventAt).getTime() : null;
    const next = this.nextPunch?.eventAt ? new Date(this.nextPunch.eventAt).getTime() : null;
    if (previous !== null && Number.isFinite(previous) && eventAt <= previous) {
      return this.translate.instant('missingPunchesCatalog.manualPunch.validationAfterPrevious');
    }
    if (next !== null && Number.isFinite(next) && eventAt >= next) {
      return this.translate.instant('missingPunchesCatalog.manualPunch.validationBeforeNext');
    }
    return null;
  }

  private toUtcIso(dateValue: string, timeValue: string, timeZone: string): string {
    const [year, month, day] = dateValue.split('-').map(Number);
    const [hour, minute] = timeValue.split(':').map(Number);
    const wallClockUtc = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
    let instant = wallClockUtc - this.timeZoneOffset(wallClockUtc, timeZone);
    instant = wallClockUtc - this.timeZoneOffset(instant, timeZone);
    return new Date(instant).toISOString();
  }

  private timeZoneOffset(timestamp: number, timeZone: string): number {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
    }).formatToParts(new Date(timestamp));
    const value = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.find((part) => part.type === type)?.value || 0);
    const representedUtc = Date.UTC(value('year'), value('month') - 1, value('day'), value('hour'), value('minute'), value('second'));
    return representedUtc - timestamp;
  }

  private notify(panelClass: string, message: string): void {
    this.snackBar.open(message, undefined, { duration: 4000, panelClass: [panelClass], horizontalPosition: 'center', verticalPosition: 'bottom' });
  }

  private get slotTranslationKey(): string {
    return ({ entry: 'entry', meal_start: 'mealStart', meal_end: 'mealEnd', exit: 'exit' } as const)[this.data.slot];
  }

  private get locale(): string { return this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX'; }
  private readonly slotOrder: MissingPunchSlot[] = ['entry', 'meal_start', 'meal_end', 'exit'];
}
